/**
 * Data Exports hooks
 *
 * tRPC hooks for the `exports.*` router (SFTP + email scheduled report
 * exports). Every procedure takes an optional `brandId` — the SNOWFLAKE
 * brand id of the brand selected in the navbar switcher, for admins and
 * brand admins alike (the server checks a brand admin's membership).
 *
 * `useExportsBrandId` mirrors the CatalogRoute.tsx brand-resolution
 * pattern, restricted to the two roles the brief grants exports access
 * to (BRAND_ADMIN, and ADMIN/SUPER_ADMIN with a brand selected):
 * - BRAND_ADMIN: `brandId` is the switcher brand's Snowflake id, the
 *   same brand the sidebar shows (a multi-brand brand admin would
 *   otherwise manage their legacy `User.brandId` brand while looking at
 *   another — final-review I4). `ready` once brands have loaded and a
 *   selection has landed. Only when the brand list is empty does it send
 *   no id and let the server resolve their own brand.
 * - ADMIN/SUPER_ADMIN: `brandId` comes from the navbar brand switcher
 *   and the hook is `ready` only once a brand is actually selected.
 * - Every other role (BRAND_MANAGER, FINANCE, ...): never `ready`. These
 *   roles have no access to exports at all — `capabilities` on the
 *   server resolves the actor's role via `ExportsAccess.resolve` and
 *   throws FORBIDDEN for them, so querying at all (even to learn the
 *   feature is off) would 403 on every page load for every brand
 *   manager and finance user, since `CompanyNavGroup` calls
 *   `useExportsCapabilities` unconditionally.
 */

import { EXPORTS_CAPABILITIES_STALE_MS, exportsBrandInput } from './v2PrefetchInputs';
import { trpc } from '../../lib/trpc';
import { usePermissions } from '../usePermissions';
import { usePortalBrand } from '../../contexts/PortalBrandContext';

export function useExportsBrandId(): { brandId: number | undefined; ready: boolean } {
  const { isBrandAdmin, isAdminOrHigher } = usePermissions();
  const { brands, selectedBrand, isLoadingBrands } = usePortalBrand();

  if (isBrandAdmin()) {
    if (isLoadingBrands) return { brandId: undefined, ready: false };
    if (selectedBrand) return { brandId: selectedBrand.snowflakeIdNum, ready: true };
    return { brandId: undefined, ready: brands.length === 0 };
  }

  if (isAdminOrHigher()) {
    return {
      brandId: selectedBrand?.snowflakeIdNum,
      ready: !isLoadingBrands && selectedBrand != null,
    };
  }

  return { brandId: undefined, ready: false };
}

/**
 * `enabled: false` never throws (it's the client's way to ask "is the
 * feature on"); disabled while `!ready` so an admin with no brand
 * selected neither crashes nor spams the server with retries.
 */
export function useExportsCapabilities() {
  const { brandId, ready } = useExportsBrandId();
  return trpc.exports.capabilities.useQuery(
    exportsBrandInput(brandId),
    { enabled: ready, retry: false, staleTime: EXPORTS_CAPABILITIES_STALE_MS },
  );
}

export function useExportConnection() {
  const { brandId, ready } = useExportsBrandId();
  return trpc.exports.connection.get.useQuery({ brandId }, { enabled: ready });
}

export function useTestConnection() {
  return trpc.exports.connection.test.useMutation();
}

export function useSaveConnection() {
  const utils = trpc.useUtils();
  return trpc.exports.connection.save.useMutation({
    onSuccess: () => {
      utils.exports.connection.get.invalidate();
      utils.exports.schedules.list.invalidate();
      utils.exports.runs.list.invalidate();
    },
  });
}

export function useGenerateKey() {
  const utils = trpc.useUtils();
  return trpc.exports.connection.generateKey.useMutation({
    onSuccess: () => {
      utils.exports.connection.get.invalidate();
      utils.exports.schedules.list.invalidate();
      utils.exports.runs.list.invalidate();
    },
  });
}

export function useRemoveConnection() {
  const utils = trpc.useUtils();
  return trpc.exports.connection.remove.useMutation({
    onSuccess: () => {
      utils.exports.connection.get.invalidate();
      utils.exports.schedules.list.invalidate();
      utils.exports.runs.list.invalidate();
    },
  });
}

export function useExportSchedules() {
  const { brandId, ready } = useExportsBrandId();
  return trpc.exports.schedules.list.useQuery(exportsBrandInput(brandId), { enabled: ready });
}

export function useCreateSchedule() {
  const utils = trpc.useUtils();
  return trpc.exports.schedules.create.useMutation({
    onSuccess: () => {
      utils.exports.schedules.list.invalidate();
      utils.exports.runs.list.invalidate();
    },
  });
}

export function useUpdateSchedule() {
  const utils = trpc.useUtils();
  return trpc.exports.schedules.update.useMutation({
    onSuccess: () => {
      utils.exports.schedules.list.invalidate();
      utils.exports.runs.list.invalidate();
    },
  });
}

export function useSetScheduleActive() {
  const utils = trpc.useUtils();
  return trpc.exports.schedules.setActive.useMutation({
    onSuccess: () => {
      utils.exports.schedules.list.invalidate();
      utils.exports.runs.list.invalidate();
    },
  });
}

export function useRemoveSchedule() {
  const utils = trpc.useUtils();
  return trpc.exports.schedules.remove.useMutation({
    onSuccess: () => {
      utils.exports.schedules.list.invalidate();
      utils.exports.runs.list.invalidate();
    },
  });
}

export function useSendNow() {
  const utils = trpc.useUtils();
  return trpc.exports.schedules.sendNow.useMutation({
    onSuccess: () => {
      utils.exports.schedules.list.invalidate();
      utils.exports.runs.list.invalidate();
    },
  });
}

export function useExportRuns(page: number) {
  const { brandId, ready } = useExportsBrandId();
  return trpc.exports.runs.list.useQuery({ brandId, page }, { enabled: ready });
}

export function useRetryDelivery() {
  const utils = trpc.useUtils();
  return trpc.exports.runs.retryDelivery.useMutation({
    onSuccess: () => {
      utils.exports.schedules.list.invalidate();
      utils.exports.runs.list.invalidate();
    },
  });
}
